import { useState, useEffect } from 'react';
import api from '../../../api/axios';
import type { TimelineEvent } from '../types/timeline.types';

interface UseEmailFeaturesProps {
  caseId: string;
  headers: any;
  fetchTimelineEvents: () => Promise<any>;
  setError: (error: string | null) => void;
}

export const useEmailFeatures = ({
  caseId,
  headers,
  fetchTimelineEvents,
  setError
}: UseEmailFeaturesProps) => {
  // Email title editing state
  const [editingEmailTitleId, setEditingEmailTitleId] = useState<string | null>(null);
  const [editEmailTitle, setEditEmailTitle] = useState<string>('');
  const [generatingEmailTitle, setGeneratingEmailTitle] = useState<boolean>(false);

  // Email tag management state
  const [availableEmailTags, setAvailableEmailTags] = useState<Array<{id: string, name: string, color: string}>>([]);
  const [tagDialogOpen, setTagDialogOpen] = useState<boolean>(false);
  const [tagDialogEventId, setTagDialogEventId] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<{id: string, name: string, color: string} | null>(null);
  const [newTagName, setNewTagName] = useState<string>('');
  const [tagOperationLoading, setTagOperationLoading] = useState<boolean>(false);

  // Email summarization state
  const [emailSummaries, setEmailSummaries] = useState<Record<string, { summary: string; loading: boolean; error: string | null }>>({});
  const [summaryPopoverAnchor, setSummaryPopoverAnchor] = useState<{ element: HTMLElement; eventId: string } | null>(null);

  // Fetch available email tags
  const fetchEmailTags = async () => {
    try {
      const response = await api.get(`/cases/${caseId}/timeline/email-tags/`, { headers });
      setAvailableEmailTags(response.data.tags || []);
    } catch (err) {
      console.error('Failed to fetch email tags:', err);
    }
  };

  // Email Title Functions
  const handleEditEmailTitle = (event: TimelineEvent) => {
    setEditingEmailTitleId(event.id);
    setEditEmailTitle(event.custom_title || event.title);
  };

  const handleGenerateEmailTitle = async (eventId: string) => {
    setGeneratingEmailTitle(true);

    try {
      const response = await api.post(
        `/cases/${caseId}/timeline/${eventId}/generate-title/`,
        {},
        { headers }
      );

      if (response.data.success && response.data.title) {
        setEditEmailTitle(response.data.title);
      }
    } catch (err: any) {
      console.error('Failed to generate email title:', err);
      // Keep the original title if AI generation fails
    } finally {
      setGeneratingEmailTitle(false);
    }
  };

  const handleSaveEmailTitle = async (eventId: string) => {
    try {
      await api.patch(`/cases/${caseId}/timeline/${eventId}/`, {
        custom_title: editEmailTitle.trim()
      }, { headers });

      await fetchTimelineEvents();
      setEditingEmailTitleId(null);
      setEditEmailTitle('');
    } catch (err) {
      console.error('Failed to update email title:', err);
      setError('Failed to update email title');
    }
  };

  const handleCancelEmailTitleEdit = () => {
    setEditingEmailTitleId(null);
    setEditEmailTitle('');
  };

  // Email Tag Functions
  const handleOpenTagDialog = (eventId: string) => {
    setTagDialogEventId(eventId);
    setTagDialogOpen(true);
    setNewTagName('');
  };

  const handleCloseTagDialog = () => {
    setTagDialogOpen(false);
    setTagDialogEventId(null);
    setNewTagName('');
  };

  const handleAddExistingTag = async (tagId: string) => {
    if (!tagDialogEventId) return;

    setTagOperationLoading(true);
    try {
      await api.post(
        `/cases/${caseId}/timeline/${tagDialogEventId}/add-tag/`,
        { tag_id: tagId },
        { headers }
      );

      await fetchTimelineEvents();
      handleCloseTagDialog();
    } catch (err) {
      console.error('Failed to add tag:', err);
      setError('Failed to add tag');
    } finally {
      setTagOperationLoading(false);
    }
  };

  const handleCreateAndAddTag = async () => {
    if (!newTagName.trim() || !tagDialogEventId) return;

    setTagOperationLoading(true);
    try {
      // Create the tag
      const createResponse = await api.post(
        `/cases/${caseId}/timeline/email-tags/create/`,
        { name: newTagName.trim() },
        { headers }
      );

      const newTag = createResponse.data.tag;

      // Add the tag to the email
      await api.post(
        `/cases/${caseId}/timeline/${tagDialogEventId}/add-tag/`,
        { tag_id: newTag.id },
        { headers }
      );

      // Refresh data
      await fetchEmailTags();
      await fetchTimelineEvents();
      handleCloseTagDialog();
    } catch (err) {
      console.error('Failed to create and add tag:', err);
      setError('Failed to create tag');
    } finally {
      setTagOperationLoading(false);
    }
  };

  const handleRemoveTag = async (eventId: string, tagId: string) => {
    try {
      await api.post(
        `/cases/${caseId}/timeline/${eventId}/remove-tag/`,
        { tag_id: tagId },
        { headers }
      );

      await fetchTimelineEvents();
    } catch (err) {
      console.error('Failed to remove tag:', err);
      setError('Failed to remove tag');
    }
  };

  // Email Summarization Functions
  const handleSummarizeEmail = async (eventId: string) => {
    setEmailSummaries(prev => ({
      ...prev,
      [eventId]: { summary: '', loading: true, error: null }
    }));

    try {
      const response = await api.post(
        `/cases/${caseId}/timeline/${eventId}/summarize-email/`,
        {},
        { headers }
      );

      setEmailSummaries(prev => ({
        ...prev,
        [eventId]: {
          summary: response.data.summary,
          loading: false,
          error: null
        }
      }));
    } catch (err: any) {
      console.error('Failed to summarize email:', err);
      const errorMessage = err.response?.data?.error || 'Failed to generate summary';
      setEmailSummaries(prev => ({
        ...prev,
        [eventId]: {
          summary: '',
          loading: false,
          error: errorMessage
        }
      }));
    }
  };

  const handleOpenSummaryPopover = (event: React.MouseEvent<HTMLElement>, eventId: string) => {
    event.stopPropagation(); // Prevent card expansion
    setSummaryPopoverAnchor({ element: event.currentTarget, eventId });

    // If summary doesn't exist yet, fetch it
    if (!emailSummaries[eventId]) {
      handleSummarizeEmail(eventId);
    }
  };

  const handleCloseSummaryPopover = () => {
    setSummaryPopoverAnchor(null);
  };

  // Fetch email tags on mount
  useEffect(() => {
    fetchEmailTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  return {
    // Email title state
    editingEmailTitleId,
    editEmailTitle,
    generatingEmailTitle,
    setEditEmailTitle,
    setEditingEmailTitleId,

    // Email tag state
    availableEmailTags,
    tagDialogOpen,
    tagDialogEventId,
    selectedTag,
    newTagName,
    tagOperationLoading,
    setSelectedTag,
    setNewTagName,

    // Email summary state
    emailSummaries,
    summaryPopoverAnchor,

    // Email title functions
    handleEditEmailTitle,
    handleGenerateEmailTitle,
    handleSaveEmailTitle,
    handleCancelEmailTitleEdit,

    // Email tag functions
    fetchEmailTags,
    handleOpenTagDialog,
    handleCloseTagDialog,
    handleAddExistingTag,
    handleCreateAndAddTag,
    handleRemoveTag,

    // Email summary functions
    handleSummarizeEmail,
    handleOpenSummaryPopover,
    handleCloseSummaryPopover,
  };
};
